import * as React from 'react';

import { type TModifier } from '@/packages/Filters';
import { FOfferType, type NonEmptyArray, getOfferTypeFromJsonQuery, isSuburban } from '@/packages/JsonQuery';

import { reduxActions, useReduxDispatch } from '@/microfrontends/filters/shared/store';
import { useJsonQuerySelector } from '@/microfrontends/filters/shared/store/slices/jsonQuery/selectors';
import { updateLocation } from '@/microfrontends/filters/shared/store/thunks/location/updateLocation';
import { tempNormalizeLocation } from '@/microfrontends/filters/shared/utils/tempNormalizeLocation';

import { trackGeoSuggestAutoOfferChange } from '../tracking';
import { type ISelectedVillage } from '../types';

type TSetVillageFunction = (village: ISelectedVillage) => Promise<NonEmptyArray<TModifier>>;

export function useSetVillage(): TSetVillageFunction {
  const dispatch = useReduxDispatch();

  const jsonQuery = useJsonQuerySelector();
  const offerType = getOfferTypeFromJsonQuery(jsonQuery.current);

  return React.useCallback<TSetVillageFunction>(
    async village => {
      // Устанавливаем новое название для КП через стор
      dispatch(reduxActions.villageName.change(village.name));

      // Устанавливаем новый id для КП в jsonQuery
      const modifiers: NonEmptyArray<TModifier> = [{ action: 'setVillage', arguments: [village.id] }];

      // При выборе КП нужно поменять тип оффера на загородку
      if (!isSuburban(offerType)) {
        modifiers.push({ action: 'setOfferType', arguments: [FOfferType.Suburban] });
        trackGeoSuggestAutoOfferChange(village.id, offerType);
      }

      // для КП сразу переключаем регион в фильтрах на тот который пришел из саджеста
      if (village.regionId) {
        const updateLocationResult = await dispatch(updateLocation({ regionId: village.regionId }));

        if (updateLocation.fulfilled.match(updateLocationResult)) {
          const prevLocation = tempNormalizeLocation(updateLocationResult.payload.prev);
          const nextLocation = tempNormalizeLocation(updateLocationResult.payload.next);

          modifiers.push({ action: 'setLocations', arguments: [{ prev: prevLocation, next: nextLocation }] });
        }
      }

      return modifiers;
    },
    [dispatch, offerType],
  );
}
